import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { dims, formatCOP, useSiteMedia } from "@/lib/site";

const GAP = 0.05;

const useWidth = () => {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
};

const Plate = ({ p, o, d, k, label, sideW, media }) => {
  const w = d.w * k, h = d.h * k, padX = w * GAP / 2 + 4, padTop = h * GAP + 6;
  const m = media[`compare-${p.slug}-${o}`];
  return (
    <div data-testid={`compare-plate-${p.slug}`} className="flex shrink-0 items-end">
      <div className="flex flex-col items-center" style={{ width: sideW, height: h }}>
        <span className="relative flex h-full w-[7px] items-center justify-center">
          <span className="absolute inset-y-0 left-1/2 w-px bg-[#B18A4B]" />
          <span className="absolute inset-x-0 top-0 h-px bg-[#B18A4B]" />
          <span className="absolute inset-x-0 bottom-0 h-px bg-[#B18A4B]" />
          <span className="relative whitespace-nowrap bg-[#FAF8F4] py-1 leading-none text-[#8A6A33] [writing-mode:vertical-rl] rotate-180" style={{ fontSize: label }}>{d.h} cm</span>
        </span>
      </div>
      <div style={{ paddingLeft: padX, paddingRight: padX }}>
        <div className="mb-1 flex flex-col items-center" style={{ width: w, marginBottom: padTop }}>
          <span data-testid={`compare-width-${p.slug}`} className="leading-none text-[#8A6A33]" style={{ fontSize: label }}>{d.w} cm</span>
          <span className="mt-1 h-[6px] w-full border-x border-t border-[#B18A4B]" />
        </div>
        <div data-testid={`compare-photo-${p.slug}`}
          className="overflow-hidden bg-white shadow-[0_14px_28px_-14px_rgba(8,8,8,0.45)] transition-[transform,box-shadow] [transition-duration:250ms] ease-out origin-bottom [@media(hover:hover)_and_(prefers-reduced-motion:no-preference)]:hover:scale-105 [@media(hover:hover)]:hover:shadow-[0_22px_40px_-16px_rgba(8,8,8,0.5)] motion-reduce:transition-none"
          style={{ width: w, height: h, borderRadius: Math.max(3, Math.min(w, h) * 0.05) }}>
          {m && <img src={m.url} alt={m.alt} className="block h-full w-full object-contain" />}
        </div>
      </div>
    </div>
  );
};

export const SizeCompare = ({ products, orientation }) => {
  const [ref, W] = useWidth();
  const media = useSiteMedia();
  const mobile = W < 640;
  const sideW = mobile ? 14 : 22, label = mobile ? 10 : 15, gap = mobile ? 4 : 24;
  const ds = products.map((p) => dims(p, orientation));
  const fixed = products.length * (sideW + 8) + (products.length - 1) * gap + 2;
  const kW = (W - fixed) / (ds.reduce((a, d) => a + d.w, 0) * (1 + GAP));
  const kH = (mobile ? 260 : 440) / Math.max(...ds.map((d) => d.h));
  const k = Math.max(0, Math.min(kW, kH));
  return (
    <div ref={ref} data-testid="size-compare" className="w-full">
      {W > 0 && (
        <>
          <div data-testid="size-compare-row" className="flex items-end justify-center" style={{ gap }}>
            {products.map((p, i) => <Plate key={p.slug} p={p} o={orientation} d={ds[i]} k={k} label={label} sideW={sideW} media={media} />)}
          </div>
          <div className="mt-8 grid grid-cols-3 gap-3 sm:gap-6">
            {products.map((p) => (
              <div key={p.slug} data-testid={`compare-col-${p.slug}`} className="flex flex-col items-center text-center">
                <p className="font-serif-display text-4xl font-medium leading-none sm:text-5xl">{p.size}</p>
                <p data-testid={`compare-price-${p.slug}`} className="mt-3 text-sm font-semibold sm:text-xl">{formatCOP(p.price_cop)}</p>
                <Link to={`/productos/${p.slug}?orientacion=${orientation}`} data-testid={`compare-choose-${p.slug}`}
                  className="mt-4 flex h-10 w-full max-w-[200px] items-center justify-center rounded-lg bg-[#080808] text-xs font-semibold uppercase tracking-[0.18em] text-[#FAF8F4] transition-colors hover:bg-[#B18A4B] sm:h-12 sm:text-sm">Elegir</Link>
              </div>
            ))}
          </div>
        </>
      )}
      <p data-testid="size-compare-note" className="mt-8 text-center text-sm italic text-[#080808]/60">Tamaños mostrados en proporción entre sí</p>
    </div>
  );
};
